// Utility helper functions for the Multi-AI Chat Demo

/**
 * Format timestamp to readable time string
 * @param {number} timestamp - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (timestamp) => {
  // Handle both relative timestamps (seconds from start) and absolute timestamps
  let date;
  
  if (timestamp > 1000000000) {
    // It's an absolute timestamp (Unix timestamp)
    date = new Date(timestamp * 1000);
  } else {
    // It's a relative timestamp (seconds from conversation start)
    const now = new Date();
    const startTime = new Date(now.getTime() - (timestamp * 1000));
    date = startTime;
  }
  
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Generate a unique ID for messages
 * @returns {string} Unique identifier
 */
export const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit function calls
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Scroll element into view with smooth behavior
 * @param {HTMLElement} element - Element to scroll to
 */
export const scrollIntoView = (element) => {
  if (element && typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'end' 
    });
  }
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error('Failed to copy text:', error);
    return false;
  }
};

/**
 * Validate message content
 * @param {string} message - Message to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {Object} Validation result
 */
export const validateMessage = (message, maxLength = 500) => {
  const trimmed = message.trim();
  
  return {
    isValid: trimmed.length > 0 && trimmed.length <= maxLength,
    length: trimmed.length,
    maxLength,
    isEmpty: trimmed.length === 0,
    isTooLong: trimmed.length > maxLength
  };
};

/**
 * Extract mentions from message text
 * @param {string} text - Message text
 * @returns {Array} Array of mentioned usernames
 */
export const extractMentions = (text) => {
  const mentionRegex = /@(\w+)/g;
  const mentions = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]);
  }
  
  return [...new Set(mentions)]; // Remove duplicates
};

/**
 * Format message with mentions highlighted
 * @param {string} text - Message text
 * @returns {string} HTML string with highlighted mentions
 */
export const formatMessageWithMentions = (text) => {
  return text.replace(
    /@(\w+)/g, 
    '<span class="mention">@$1</span>'
  );
};

/**
 * Calculate reading time for text
 * @param {string} text - Text to analyze
 * @returns {number} Reading time in minutes
 */
export const calculateReadingTime = (text) => {
  const wordsPerMinute = 200; // Average reading speed
  const wordCount = text.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Get browser information
 * @returns {Object} Browser details
 */
export const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(navigator.vendor);
  const isEdge = /Edg/.test(userAgent);
  
  return {
    userAgent,
    isChrome,
    isFirefox,
    isSafari,
    isEdge,
    isMobile: /Mobi|Android/i.test(userAgent)
  };
};

/**
 * Check if user prefers reduced motion
 * @returns {boolean} True if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Generate a random delay for typing simulation
 * @param {number} min - Minimum delay in milliseconds
 * @param {number} max - Maximum delay in milliseconds
 * @returns {number} Random delay
 */
export const getRandomTypingDelay = (min = 1000, max = 3000) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};
